import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    BadgePercent,
    BarChart3,
    CalendarDays,
    CheckCircle2,
    Clock,
    Download,
    FileSpreadsheet,
    Landmark,
    ReceiptText,
    RefreshCcw,
    WalletCards,
} from "lucide-react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";
import "./DashboardPaie.css";

const COLORS = {
    primary: "#204385",
    secondary: "#334785",
    success: "#4caf50",
    warning: "#ff9800",
    error: "#f44336",
    info: "#2196f3",
    purple: "#9c27b0",
};

const CHART_COLORS = [
    COLORS.primary,
    COLORS.secondary,
    COLORS.success,
    COLORS.warning,
    COLORS.info,
    COLORS.purple,
];

const today = new Date();
const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);

const payrollData = {
    totals: {
        masseBrute: 428500,
        netAPayer: 316840,
        cotisations: 42730,
        ir: 68930,
        bulletins: 118,
        collaborateurs: 126,
        completion: 94,
        validationDelay: 1.6,
    },
    monthlyTrend: [
        { month: "Jan", brut: 362000, net: 268000 },
        { month: "Fev", brut: 376000, net: 279000 },
        { month: "Mar", brut: 389000, net: 286000 },
        { month: "Avr", brut: 401000, net: 298000 },
        { month: "Mai", brut: 414000, net: 306500 },
        { month: "Juin", brut: 428500, net: 316840 },
    ],
    costSplit: [
        { name: "Net a payer", value: 316840 },
        { name: "Cotisations", value: 42730 },
        { name: "IR", value: 68930 },
    ],
    cotisationsByType: [
        { name: "CNSS", amount: 25400 },
        { name: "AMO", amount: 11820 },
        { name: "Formation", amount: 5510 },
    ],
    bulletinStatus: [
        { label: "Calculés", value: 118, tone: "success" },
        { label: "Restants", value: 8, tone: "warning" },
        { label: "Validés", value: 96, tone: "info" },
        { label: "Clôturés", value: 73, tone: "primary" },
    ],
};

const excelReports = [
    {
        title: "Etat recapitulatif de paie",
        description: "Synthese par collaborateur: brut, retenues, IR et net a payer.",
        columns: "Collaborateur, periode, brut, retenues, net",
        icon: FileSpreadsheet,
    },
    {
        title: "Etat CNSS / AMO",
        description: "Bases de cotisation, parts salariales et patronales par taux.",
        columns: "Matricule, base, taux, part salariale, part patronale",
        icon: BadgePercent,
    },
    {
        title: "Etat IR",
        description: "RNI, tranche appliquee, taux, deduction et montant IR.",
        columns: "Collaborateur, RNI, taux, deduction, IR",
        icon: Landmark,
    },
    {
        title: "Journal des variables",
        description: "Primes, heures supplementaires et elements variables saisis.",
        columns: "Collaborateur, rubrique, valeur, commentaire",
        icon: ReceiptText,
    },
];

const formatMoney = (value) => `${Number(value).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} MAD`;

function StatCard({ title, value, subtitle, icon, color, trend }) {
    const positive = typeof trend === "string" && trend.startsWith("+");
    return (
        <div className="dpStatCard">
            <div className="dpStatCard__row">
                <div>
                    <div className="dpStatCard__title">{title}</div>
                    <div className="dpStatCard__value" style={{ color }}>{value}</div>
                    {subtitle && <div className="dpStatCard__sub">{subtitle}</div>}
                    {trend && <span className={`dpTrend ${positive ? "is-plus" : "is-minus"}`}>{trend}</span>}
                </div>
                <div className="dpStatIcon" style={{ color, background: `${color}15`, borderColor: `${color}25` }}>
                    {icon}
                </div>
            </div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="dpChartCard">
            <div className="dpChartCard__title">{title}</div>
            <div className="dpChartCard__body">{children}</div>
        </div>
    );
}

function SectionTitle({ icon, children }) {
    return (
        <div className="dpSectionTitle">
            <span className="dpSectionTitle__icon">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function GaugeCard({ value }) {
    const angle = Math.round((value / 100) * 360);
    return (
        <div className="dpGaugeCard">
            <div className="dpGauge" style={{ background: `conic-gradient(${COLORS.success} ${angle}deg, #eef1f5 0deg)` }}>
                <div className="dpGaugeInner">
                    <strong>{value}%</strong>
                    <span>Avancement</span>
                </div>
            </div>
            <div className="dpGaugeText">
                <div>Cycle de paie</div>
                <small>118 bulletins calcules sur 126</small>
            </div>
        </div>
    );
}

export default function DashboardPaie() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("kpis");
    const [filters, setFilters] = useState({ dateFrom: firstDay, dateTo: lastDay });
    const [loading, setLoading] = useState(false);

    const lastUpdated = useMemo(() => new Date().toLocaleString("fr-FR"), [loading]);
    const periodLabel = useMemo(() => {
        const from = filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString("fr-FR") : "-";
        const to = filters.dateTo ? new Date(filters.dateTo).toLocaleDateString("fr-FR") : "-";
        return `${from} au ${to}`;
    }, [filters]);

    const refresh = () => {
        setLoading(true);
        setTimeout(() => setLoading(false), 700);
    };

    return (
        <div className="dpPage">
            <div className="dpMainTabs">
                <button className={`dpMainTab ${activeTab === "kpis" ? "active" : ""}`} type="button" onClick={() => setActiveTab("kpis")}>
                    KPIs
                </button>
                <button className={`dpMainTab ${activeTab === "etats" ? "active" : ""}`} type="button" onClick={() => setActiveTab("etats")}>
                    Etats Excel
                </button>
                {/*<button className="dpMainTab" type="button" onClick={() => navigate("/paie/documents")}>*/}
                {/*    Centre de Documents*/}
                {/*</button>*/}
            </div>

            <header className="dpHeader">
                <div>
                    <h1><BarChart3 size={18} />Tableau de bord paie</h1>
                    <p>Vue statique des indicateurs, repartitions et exports de paie.</p>
                </div>
                <button className="dpBtn dpBtn--primary" type="button" onClick={refresh}>
                    <RefreshCcw size={16} />
                    {loading ? "Actualisation..." : "Rafraichir"}
                </button>
            </header>

            <main className="dpMain">
                <div className="dpFilters">
                    <div className="dpDateField">
                        <label>Date de</label>
                        <input type="date" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} />
                    </div>
                    <div className="dpDateField">
                        <label>Date a</label>
                        <input type="date" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} />
                    </div>
                    <div className="dpPeriodBadge">
                        <CalendarDays size={15} />
                        {periodLabel}
                    </div>
                </div>

                {activeTab === "kpis" ? (
                    <>
                        <SectionTitle icon={<WalletCards size={18} />}>Indicateurs generaux paie</SectionTitle>
                        <div className="dpGrid dpGrid--4">
                            <StatCard title="Masse salariale brute" value={formatMoney(payrollData.totals.masseBrute)} subtitle="Brut imposable total" icon={<WalletCards size={20} />} color={COLORS.primary} trend="+4.2%" />
                            <StatCard title="Net a payer total" value={formatMoney(payrollData.totals.netAPayer)} subtitle="Apres retenues et IR" icon={<ReceiptText size={20} />} color={COLORS.success} trend="+2.8%" />
                            <StatCard title="Cotisations salariales" value={formatMoney(payrollData.totals.cotisations)} subtitle="CNSS, AMO et autres" icon={<BadgePercent size={20} />} color={COLORS.secondary} trend="-1.1%" />
                            <StatCard title="IR total" value={formatMoney(payrollData.totals.ir)} subtitle="Impot sur le revenu" icon={<Landmark size={20} />} color={COLORS.warning} trend="+3.6%" />
                        </div>

                        <div className="dpGrid dpGrid--3">
                            <GaugeCard value={payrollData.totals.completion} />
                            <div className="dpMiniStatusCard">
                                <div className="dpMiniStatusTitle"><CheckCircle2 size={16} />Situation bulletins</div>
                                <div className="dpMiniStatusGrid">
                                    {payrollData.bulletinStatus.map((item) => (
                                        <div className={`dpMiniStatus dpMiniStatus--${item.tone}`} key={item.label}>
                                            <strong>{item.value}</strong>
                                            <span>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="dpDelayCard">
                                <div className="dpDelayIcon"><Clock size={20} /></div>
                                <strong>{payrollData.totals.validationDelay}j</strong>
                                <span>Delai moyen de validation</span>
                                <div className="dpDelayScale"><span style={{ width: "38%" }} /></div>
                            </div>
                        </div>

                        <SectionTitle icon={<BarChart3 size={18} />}>Analyses graphiques</SectionTitle>
                        <div className="dpGrid dpGrid--2">
                            <ChartCard title="Evolution brut et net">
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={payrollData.monthlyTrend}>
                                        <defs>
                                            <linearGradient id="dpBrut" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.75} />
                                                <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.08} />
                                            </linearGradient>
                                            <linearGradient id="dpNet" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.55} />
                                                <stop offset="95%" stopColor={COLORS.success} stopOpacity={0.06} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Area type="monotone" dataKey="brut" stroke={COLORS.primary} fill="url(#dpBrut)" strokeWidth={2} />
                                        <Area type="monotone" dataKey="net" stroke={COLORS.success} fill="url(#dpNet)" strokeWidth={2} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </ChartCard>

                            <ChartCard title="Repartition du cout de paie">
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie data={payrollData.costSplit} cx="50%" cy="50%" innerRadius={60} outerRadius={105} paddingAngle={3} dataKey="value" label={({ name }) => name}>
                                            {payrollData.costSplit.map((_, index) => (
                                                <Cell key={`cost-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>

                            <ChartCard title="Cotisations par type">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={payrollData.cotisationsByType} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis type="number" />
                                        <YAxis dataKey="name" type="category" width={110} />
                                        <RechartsTooltip />
                                        <Bar dataKey="amount" fill={COLORS.secondary} radius={[0, 8, 8, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>

                            <ChartCard title="Tendance du net a payer">
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={payrollData.monthlyTrend}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Line type="monotone" dataKey="net" stroke={COLORS.info} strokeWidth={3} dot={{ r: 4 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </div>
                    </>
                ) : (
                    <>
                        <SectionTitle icon={<FileSpreadsheet size={18} />}>Etats Excel disponibles</SectionTitle>
                        <div className="dpReportGrid">
                            {excelReports.map((report, index) => {
                                const Icon = report.icon;
                                return (
                                    <div className="dpReportCard" key={report.title}>
                                        <div className="dpReportIcon" style={{ color: CHART_COLORS[index % CHART_COLORS.length], background: `${CHART_COLORS[index % CHART_COLORS.length]}15`, borderColor: `${CHART_COLORS[index % CHART_COLORS.length]}25` }}>
                                            <Icon size={20} />
                                        </div>
                                        <div className="dpReportBody">
                                            <h3>{report.title}</h3>
                                            <p>{report.description}</p>
                                            <span>{report.columns}</span>
                                        </div>
                                        <button className="dpBtn dpBtn--primary" type="button">
                                            <Download size={14} />
                                            Excel
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                <div className="dpFooter">Derniere mise a jour : {lastUpdated}</div>
            </main>
        </div>
    );
}
